import React, { useEffect, useState, useRef } from "react";
import { mapOrderApiService } from "../../../service/admin/mapOrderApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import Pagination from "../../../component/library/pagination";
import ExcelJS from "exceljs";
import { FaUpload, FaDownload, FaSearch, FaTrash, FaExternalLinkAlt } from "react-icons/fa";
import "./style.css";

const MapOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 0, totalItems: 0 });
  const [stats, setStats] = useState({ total: 0, pending: 0, mapped: 0, failed: 0, today: 0, week: 0 });
  const [deleteModal, setDeleteModal] = useState({ open: false, order: null });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const loader = useLoader();
  const message = useMessage();
  const fileInputRef = useRef(null);

  // Fix wrongly-failed rows on first load (one-time cleanup)
  useEffect(() => {
    const token = localStorage.getItem("LOGIN_HEADER");
    fetch(`${process.env.REACT_APP_APIUrl || "https://api.importerr.com/api/"}admin/map-orders/check-pending`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchMapOrders(); }, [page, statusFilter, platformFilter]);

  const fetchMapOrders = () => {
    setLoading(true);
    const q = `?limit=${limit}&page=${page}&search=${search}&status=${statusFilter}&platform=${platformFilter}`;
    loader.showInlineLoader("Loading map orders...", "medium");
    mapOrderApiService.getMapOrders(q).then((res) => {
      loader.hideLoader(); setLoading(false);
      if (res.status.code === 200) {
        setOrders(res.data.orders || []);
        setPages({ currentPage: res.data.currentPage, totalPages: res.data.totalPages, totalItems: res.data.totalOrders });
        setStats({ total: res.data.totalOrders || 0, pending: res.data.pendingOrders || 0, mapped: res.data.mappedOrders || 0, failed: res.data.failedOrders || 0, today: res.data.todayOrders || 0, week: res.data.weekOrders || 0 });
      } else { setOrders([]); }
    }).catch(() => { loader.hideLoader(); setLoading(false); setOrders([]); });
  };

  const handleSearch = () => { setPage(1); fetchMapOrders(); };
  const handleUploadClick = () => { fileInputRef.current?.click(); };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploadProgress(0); setIsUploading(true);
    const interval = setInterval(() => { setUploadProgress((p) => (p >= 90 ? p : p + 10)); }, 200);
    try {
      const res = await mapOrderApiService.uploadMapOrders(formData);
      clearInterval(interval); setIsUploading(false); setUploadProgress(100);
      if (res.status.code === 200) {
        const { createdOrders, duplicateOrders, failedOrders } = res.data;
        let msg = `Uploaded: ${createdOrders} created`;
        if (duplicateOrders > 0) msg += `, ${duplicateOrders} duplicates skipped`;
        if (failedOrders > 0) msg += `, ${failedOrders} failed`;
        message.showToast(msg, ToastTypes.SUCCESS);
        setPage(1); fetchMapOrders();
      } else { message.showToast(res.error?.message || "Upload failed", ToastTypes.ERROR); }
    } catch { clearInterval(interval); setIsUploading(false); message.showToast("Error uploading file", ToastTypes.ERROR); }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Map Orders Template");
    const headers = ["ASIN", "OfferId", "Platform"];
    const hr = ws.addRow(headers);
    hr.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
      cell.font = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    ws.addRow(["B0EXAMPLE1", "12345678", "amazon"]);
    ws.addRow(["33800257", "", "myntra"]);
    headers.forEach((h, i) => { ws.getColumn(i + 1).width = Math.max(h.length + 4, 15); });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.setAttribute("download", "Map_Orders_Template.xlsx");
    document.body.appendChild(link); link.click(); link.remove();
    window.URL.revokeObjectURL(url);
  };

  // When clicking ASIN, open scraper in new tab with platform + offerId passed as params
  const handleAsinClick = (order) => {
    const plat = order.platform === "myntra" ? "myntra" : "amazon";
    // Pass offerId so the right panel auto-fills and auto-searches
    const offerParam = order.offerId ? `&offerId=${encodeURIComponent(order.offerId)}` : "";
    const variantParam = order.variantName ? `&variantName=${encodeURIComponent(order.variantName)}` : "";
    window.open(`/admin/map-order-scraper?asin=${encodeURIComponent(order.asin)}&platform=${plat}&mapOrderId=${order._id}${offerParam}${variantParam}`, "_blank");
  };

  const handleDelete = async () => {
    if (!deleteModal.order) return;
    try {
      const res = await mapOrderApiService.deleteMapOrder(deleteModal.order._id);
      if (res.status.code === 200) { message.showToast("Deleted", ToastTypes.SUCCESS); setDeleteModal({ open: false, order: null }); fetchMapOrders(); }
    } catch { message.showToast("Failed to delete", ToastTypes.ERROR); }
  };

  const getStatusClass = (s) => ({ pending: "mo-badge-pending", mapped: "mo-badge-mapped", failed: "mo-badge-failed", failed_mapped: "mo-badge-failed-mapped" }[s] || "mo-badge-pending");
  const getPlatformBadge = (p) => p === "myntra" ? "mo-platform-myntra" : "mo-platform-amazon";
  const getUploaderName = (o) => { const u = o.uploadedBy; if (!u) return "-"; return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "-"; };
  const getMappedByName = (o) => { const u = o.mappedBy; if (!u) return "-"; return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "-"; };

  return (
    <div className="mo-page">
      <div className="mo-header">
        <h2 className="mo-title">Map Orders</h2>
        <div className="mo-header-actions">
          <button className="mo-upload-btn" onClick={handleUploadClick} disabled={isUploading}>
            <FaUpload /> {isUploading ? `Uploading ${uploadProgress}%` : "Upload ASINs"}
          </button>
          <button className="mo-download-btn" onClick={handleDownloadTemplate}>
            <FaDownload /> Download Template
          </button>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".xlsx,.xls,.csv" style={{ display: "none" }} />
        </div>
      </div>

      <div className="mo-toolbar">
        <div className="mo-search-box">
          <input className="mo-search-input" placeholder="Search by ASIN, offerID, product name..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyUp={(e) => e.key === "Enter" && handleSearch()} />
          <select className="mo-platform-filter" value={platformFilter} onChange={(e) => { setPlatformFilter(e.target.value); setPage(1); }}>
            <option value="">All Platforms</option>
            <option value="amazon">Amazon</option>
            <option value="myntra">Myntra</option>
          </select>
          <button className="mo-search-btn" onClick={handleSearch}><FaSearch /> Search</button>
        </div>
        <div className="mo-filter-tabs">
          <span className={`mo-filter-tab ${statusFilter === "" ? "mo-filter-active" : ""}`} onClick={() => { setStatusFilter(""); setPage(1); }}>All <b>{stats.total}</b></span>
          <span className={`mo-filter-tab mo-filter-pending ${statusFilter === "pending" ? "mo-filter-active" : ""}`} onClick={() => { setStatusFilter("pending"); setPage(1); }}>Pending <b>{stats.pending}</b></span>
          <span className={`mo-filter-tab mo-filter-mapped ${statusFilter === "mapped" ? "mo-filter-active" : ""}`} onClick={() => { setStatusFilter("mapped"); setPage(1); }}>Mapped <b>{stats.mapped}</b></span>
          <span className={`mo-filter-tab mo-filter-failed ${statusFilter === "failed" ? "mo-filter-active" : ""}`} onClick={() => { setStatusFilter("failed"); setPage(1); }}>Failed <b>{stats.failed}</b></span>
        </div>
        <div className="mo-date-stats">Today: {stats.today} | This Week: {stats.week}</div>
      </div>

      <div className="mo-content">
        <table className="mo-table">
          <thead>
            <tr>
              <th>S.NO.</th><th>ASIN</th><th>OFFER ID</th><th>PLATFORM</th><th>STATUS</th><th>MAPPED SKU</th>
              <th>PRODUCT NAME</th><th>VARIANT NAME</th><th>CATEGORY</th><th>UPLOADED BY</th><th>MAPPED BY</th><th>CREATED AT</th><th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan="13" className="mo-empty"><div className="mo-empty-content"><p>No map orders found</p><p className="mo-empty-sub">Upload an Excel file with ASINs to get started</p></div></td></tr>
            ) : orders.map((order, idx) => (
              <tr key={order._id} className={order.status === "failed" || order.status === "failed_mapped" ? "mo-row-failed" : ""}>
                <td>{(page - 1) * limit + idx + 1}</td>
                <td>
                  <span className="mo-asin-link" onClick={() => handleAsinClick(order)} title={`Open ${order.platform === "myntra" ? "Myntra" : "Amazon"} scraper`}>
                    <span>{order.asin}</span> <FaExternalLinkAlt size={10} />
                  </span>
                </td>
                <td>
                  <div>
                    {order.mappedOfferId && order.mappedOfferId !== order.offerId ? (
                      <>
                        <span className="mo-offerid-failed">{order.offerId || "-"}</span>
                        <div className="mo-offerid-mapped">{order.mappedOfferId}</div>
                      </>
                    ) : order.status === "failed" ? (
                      <span className="mo-offerid-failed">{order.offerId || "-"}</span>
                    ) : (
                      <span>{order.offerId || "-"}</span>
                    )}
                  </div>
                </td>
                <td><span className={`mo-platform-badge ${getPlatformBadge(order.platform)}`}>{order.platform || "amazon"}</span></td>
                <td>
                  {order.status === "failed_mapped" ? (
                    <div style={{ display: "flex", gap: 3 }}>
                      <span className="mo-badge mo-badge-failed">Failed</span>
                      <span className="mo-badge mo-badge-mapped">Mapped</span>
                    </div>
                  ) : (
                    <span className={`mo-badge ${getStatusClass(order.status)}`}>{order.status}</span>
                  )}
                </td>
                <td>{order.mappedSku || "-"}</td>
                <td className="mo-product-name">{order.productName || "-"}</td>
                <td className="mo-variant-name" title={order.variantName || ""}>{order.variantName || "-"}</td>
                <td className="mo-category-cell" title={order.categoryPath || ""}>
                  {order.categoryPath || "-"}
                </td>
                <td>{getUploaderName(order)}</td>
                <td>{getMappedByName(order)}</td>
                <td>{order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN") : "-"}</td>
                <td>
                  <div className="mo-actions">
                    <button className="mo-action-btn mo-action-delete" onClick={() => setDeleteModal({ open: true, order })} title="Delete"><FaTrash /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages.totalPages > 1 && <Pagination pages={pages} setPage={setPage} />}

      {deleteModal.open && (
        <div className="mo-modal-overlay" onClick={() => setDeleteModal({ open: false, order: null })}>
          <div className="mo-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Map Order</h3>
            <p>Are you sure you want to delete ASIN: <b>{deleteModal.order?.asin}</b>?</p>
            <div className="mo-modal-actions">
              <button className="mo-modal-cancel" onClick={() => setDeleteModal({ open: false, order: null })}>Cancel</button>
              <button className="mo-modal-delete" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {isUploading && (
        <div className="mo-upload-progress-overlay">
          <div className="mo-upload-progress-modal">
            <p>Uploading file...</p>
            <div className="mo-progress-bar"><div className="mo-progress-fill" style={{ width: `${uploadProgress}%` }}></div></div>
            <p>{uploadProgress}%</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapOrders;
